import React from 'react';
import { OpenImgContextProvider } from 'openimg/react';
import {
  data,
  Form,
  Link,
  Links,
  Meta,
  Outlet,
  redirect,
  Scripts,
  ScrollRestoration,
  useLoaderData,
  useMatches,
  useRouteError,
  isRouteErrorResponse,
} from 'react-router';
import { HoneypotProvider } from 'remix-utils/honeypot/react';
import { type Route } from './+types/root.ts';
import appleTouchIconAssetUrl from './assets/favicons/apple-touch-icon.png';
import faviconAssetUrl from './assets/favicons/favicon.svg';
import { CarbonShell } from './components/carbon-shell.tsx';
import { GeneralErrorBoundary } from './components/error-boundary.tsx';
import { NotFound } from './components/not-found.tsx';
import { Forbidden } from './components/forbidden.tsx';
import { ServerError } from './components/server-error.tsx';
import { GlobalProgress } from './components/progress-bar.tsx';
import { SearchBar } from './components/search-bar.tsx';
import { useToast } from './components/toaster.tsx';
import { Button } from './components/ui/button.tsx';
import { href as iconsHref } from './components/ui/icon.tsx';
// import { EpicToaster } from './components/ui/sonner.tsx';
// import { UserDropdown } from './components/user-dropdown.tsx';
// import {
//   ThemeSwitch,
//   useOptionalTheme,
//   useTheme,
// } from './routes/resources/theme-switch.tsx';
import tailwindStyleSheetUrl from './styles/tailwind.css?url';
import { getUserId, logout } from './utils/auth.server.ts';
import { ClientHintCheck, getHints } from './utils/client-hints.tsx';
import { prisma } from './utils/db.server.ts';
import { getEnv } from './utils/env.server.ts';
import { pipeHeaders } from './utils/headers.server.ts';
import { honeypot } from './utils/honeypot.server.ts';
import { combineHeaders, getDomainUrl, getImgSrc } from './utils/misc.tsx';
import { useNonce } from './utils/nonce-provider.ts';
import { type Theme, getTheme } from './utils/theme.server.ts';
import { makeTimings, time } from './utils/timing.server.ts';
import { getToast } from './utils/toast.server.ts';
import { useOptionalUser } from './utils/user.ts';
import { getSystemName, isSystemOnline } from './utils/system-name.server.ts';
import { userHasRole } from './utils/permissions.server.ts';
import { isStaleBuildOrNetworkError } from './utils/stale-build-error.ts';
import {
  AnimatedNotification,
  useAnimatedNotification,
} from './components/animated-notification.tsx';
import { NotifyProvider } from './components/notify.tsx';
import { IdleWarningModal } from './components/idle-warning-modal.tsx';
import { useSimpleIdleTimeout } from './hooks/useSimpleIdleTimeout.ts';
import { TailwindIndicator } from '#app/components/tw-indicator.tsx';
import { DeploymentOverlay } from './components/deployment-overlay.tsx';

export const links: Route.LinksFunction = () => {
  return [
    // Preload svg sprite as a resource to avoid render blocking
    { rel: 'preload', href: iconsHref, as: 'image' },
    {
      rel: 'icon',
      href: '/favicon.ico',
      sizes: '48x48',
    },
    { rel: 'icon', type: 'image/svg+xml', href: faviconAssetUrl },
    { rel: 'apple-touch-icon', href: appleTouchIconAssetUrl },
    {
      rel: 'manifest',
      href: '/site.webmanifest',
      crossOrigin: 'use-credentials',
    } as const, // necessary to make typescript happy
    { rel: 'stylesheet', href: tailwindStyleSheetUrl },
  ].filter(Boolean);
};

export const meta: Route.MetaFunction = ({ data }) => {
  const name = data?.systemName ?? 'Bahandi';
  return [
    { title: data ? name : `Error | ${name}` },
    {
      name: 'description',
      content: `Comprehensive HR management system with time tracking, payroll processing, employee management, and project-based work logging`,
    },
  ];
};

export async function loader({ request }: Route.LoaderArgs) {
  const timings = makeTimings('root loader');
  const userId = await time(() => getUserId(request), {
    timings,
    type: 'getUserId',
    desc: 'getUserId in root',
  });

  // Check if admin is impersonating
  const { authSessionStorage } = await import('#app/utils/session.server.ts');
  const authSession = await authSessionStorage.getSession(
    request.headers.get('cookie'),
  );
  const impersonatedUserId = authSession.get('impersonatedUserId');
  const originalUserId = authSession.get('originalUserId');

  const user = userId
    ? await time(
        () =>
          prisma.user.findUnique({
            select: {
              id: true,
              firstName: true,
              middleName: true,
              lastName: true,
              suffix: true,
              nickname: true,
              username: true,
              image: { select: { objectKey: true } },
              profile: { select: { preferredName: true } },
              roles: {
                select: {
                  name: true,
                  permissions: {
                    select: { entity: true, action: true, access: true },
                  },
                },
              },
            },
            where: { id: userId },
          }),
        { timings, type: 'find user', desc: 'find user in root' },
      )
    : null;
  if (userId && !user) {
    console.info('something weird happened');
    // something weird happened... The user is authenticated but we can't find
    // them in the database. Maybe they were deleted? Let's log them out.
    await logout({ request, redirectTo: '/' });
  }

  // Run independent async operations in parallel
  const [
    systemOnline,
    isAdmin,
    { toast, headers: toastHeaders },
    honeyProps,
    systemName,
  ] = await Promise.all([
    isSystemOnline(),
    userId ? userHasRole(userId, 'admin') : Promise.resolve(false),
    getToast(request),
    honeypot.getInputProps(),
    getSystemName(),
  ]);

  // Define routes that should be accessible during maintenance mode
  const maintenanceAllowedRoutes = [
    '/under-construction',
    '/login',
    '/logout',
    '/auth',
    '/nexus',
    '/verify',
    '/onboarding',
    '/forgot-password',
    '/reset-password',
    '/resources',
    '/healthcheck',
  ];

  const url = new URL(request.url);
  const isAllowedRoute = maintenanceAllowedRoutes.some((route) =>
    url.pathname.startsWith(route),
  );

  // If system is offline and user is not admin, redirect to maintenance page
  // Allow access to authentication routes so admins can log in
  if (!systemOnline && !isAdmin && !isAllowedRoute) {
    throw redirect('/under-construction');
  }

  // Load recent notifications for the user
  // For admin/HR users: show BOTH personal notifications AND shared admin/ops notifications
  const isAdminOrHR = user?.roles?.some(
    (role) => role.name === 'admin' || role.name === 'hr_manager',
  ) ?? false;

  let notifications: Array<any> = [];
  let unreadNotificationCount = 0;

  if (userId) {
    if (isAdminOrHR) {
      // Admin/HR: Get personal notifications + all shared admin/ops notifications

      // Get user's department (for teammate filtering)
      const employmentRecord = await prisma.employmentRecord.findFirst({
        where: {
          userId,
          isActive: true,
        },
        select: {
          departmentId: true,
        },
      });

      // Get all users in the same department (teammates) in parallel
      const teammateIds = new Set<string>();
      if (employmentRecord?.departmentId) {
        const [primaryTeammates, secondaryTeammates] = await Promise.all([
          // Primary teammates
          prisma.employmentRecord.findMany({
            where: {
              departmentId: employmentRecord.departmentId,
              isActive: true,
            },
            select: { userId: true },
          }),
          // Secondary teammates
          prisma.secondaryPosition.findMany({
            where: {
              departmentId: employmentRecord.departmentId,
              isActive: true,
              employmentRecord: {
                isActive: true,
              },
            },
            select: {
              employmentRecord: {
                select: { userId: true },
              },
            },
          }),
        ]);

        primaryTeammates.forEach((t) => teammateIds.add(t.userId));
        secondaryTeammates.forEach((t) => teammateIds.add(t.employmentRecord.userId));
      }

      const [personalNotifs, sharedNotifs] = await Promise.all([
        // Personal notifications (sent directly to this user)
        prisma.notificationRecipient.findMany({
          where: { userId },
          orderBy: { createdAt: 'desc' },
          take: 10,
          include: {
            notification: {
              include: {
                sender: {
                  select: {
                    id: true,
                    firstName: true,
                    lastName: true,
                    username: true,
                  },
                },
              },
            },
          },
        }),
        // Shared admin/ops notifications (all notifications with these categories)
        prisma.notification.findMany({
          where: {
            category: {
              in: ['action_required', 'admin_ops', 'company_wide'],
            },
          },
          orderBy: { createdAt: 'desc' },
          take: 10,
          include: {
            sender: {
              select: {
                id: true,
                firstName: true,
                lastName: true,
                username: true,
              },
            },
            recipients: {
              where: { userId },
              select: {
                isRead: true,
                readAt: true,
              },
            },
          },
        }),
      ]);

      // Transform and filter personal notifications
      // For 'team' category, only include if sender is a teammate
      const personalTransformed = personalNotifs
        .map((nr) => ({
          ...nr.notification,
          isRead: nr.isRead,
          readAt: nr.readAt,
        }))
        .filter((notif) => {
          // If it's a team notification, verify the sender is actually a teammate
          if (notif.category === 'team') {
            return notif.senderId && teammateIds.has(notif.senderId);
          }
          // Personal notifications always included
          return true;
        });

      // Transform shared notifications
      const sharedTransformed = sharedNotifs.map((n) => ({
        ...n,
        isRead: n.recipients[0]?.isRead ?? false,
        readAt: n.recipients[0]?.readAt ?? null,
        recipients: undefined,
      }));

      // Combine and deduplicate by id, sort by date
      const combined = [...personalTransformed, ...sharedTransformed];
      const uniqueMap = new Map(combined.map((n) => [n.id, n]));
      notifications = Array.from(uniqueMap.values())
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
        .slice(0, 20);

      // Count unread from combined sources
      const [personalUnreadRecipients, sharedUnreadNotifications] = await Promise.all([
        // Personal unread (as recipient)
        prisma.notificationRecipient.findMany({
          where: {
            userId,
            isRead: false,
          },
          select: { notificationId: true },
        }),
        // Shared unread (notifications where user hasn't read them yet, or no recipient record exists)
        prisma.notification.findMany({
          where: {
            category: {
              in: ['action_required', 'admin_ops', 'company_wide'],
            },
          },
          select: {
            id: true,
            recipients: {
              where: { userId },
              select: { isRead: true },
            },
          },
        }),
      ]);

      // Get unique unread notification IDs
      const personalUnreadIds = new Set(personalUnreadRecipients.map((nr) => nr.notificationId));
      const sharedUnreadIds = sharedUnreadNotifications
        .filter((n) => n.recipients[0]?.isRead === false || !n.recipients[0])
        .map((n) => n.id);

      // Combine and deduplicate unread
      const allUnreadIds = new Set([...personalUnreadIds, ...sharedUnreadIds]);

      // For admin/HR: also count action_required notifications (even if read)
      // These are pending actions that should show bell indicator
      const actionRequiredCount = await prisma.notification.count({
        where: {
          category: 'action_required',
        },
      });

      // Show indicator if: (unread count > 0) OR (action required count > 0)
      unreadNotificationCount = allUnreadIds.size + actionRequiredCount;
    } else {
      // Regular employee: only personal and team notifications (filtered by actual teammates)

      // Get user's department (for teammate filtering)
      const employmentRecord = await prisma.employmentRecord.findFirst({
        where: {
          userId,
          isActive: true,
        },
        select: {
          departmentId: true,
        },
      });

      // Get all users in the same department (teammates) in parallel
      const teammateIds = new Set<string>();
      if (employmentRecord?.departmentId) {
        const [primaryTeammates, secondaryTeammates] = await Promise.all([
          // Primary teammates
          prisma.employmentRecord.findMany({
            where: {
              departmentId: employmentRecord.departmentId,
              isActive: true,
            },
            select: { userId: true },
          }),
          // Secondary teammates
          prisma.secondaryPosition.findMany({
            where: {
              departmentId: employmentRecord.departmentId,
              isActive: true,
              employmentRecord: {
                isActive: true,
              },
            },
            select: {
              employmentRecord: {
                select: { userId: true },
              },
            },
          }),
        ]);

        primaryTeammates.forEach((t) => teammateIds.add(t.userId));
        secondaryTeammates.forEach((t) => teammateIds.add(t.employmentRecord.userId));
      }

      const notificationRecipients = await prisma.notificationRecipient.findMany({
        where: { userId },
        orderBy: { createdAt: 'desc' },
        take: 20,
        include: {
          notification: {
            include: {
              sender: {
                select: {
                  id: true,
                  firstName: true,
                  lastName: true,
                  username: true,
                },
              },
            },
          },
        },
      });

      // Filter notifications: for 'team' category, only include if sender is a teammate
      notifications = notificationRecipients
        .map((nr) => ({
          ...nr.notification,
          isRead: nr.isRead,
          readAt: nr.readAt,
        }))
        .filter((notif) => {
          // If it's a team notification, verify the sender is actually a teammate
          if (notif.category === 'team') {
            return notif.senderId && teammateIds.has(notif.senderId);
          }
          // Personal notifications always included
          return true;
        });

      // Count unread notifications
      const unreadCount = await prisma.notificationRecipient.count({
        where: {
          userId,
          isRead: false,
        },
      });

      // Also count action_required notifications (even if read) for employees
      // These require action and should show bell indicator
      const actionRequiredCount = await prisma.notificationRecipient.count({
        where: {
          userId,
          notification: {
            category: 'action_required',
          },
        },
      });

      // Show indicator if: (unread count > 0) OR (action required count > 0)
      unreadNotificationCount = unreadCount + actionRequiredCount;
    }
  }

  // Load session settings and deployment mode in parallel
  const [sessionSettings, deploymentModeSetting] = await Promise.all([
    prisma.setting.findMany({
      where: { group: 'session' },
    }),
    prisma.setting.findUnique({
      where: {
        group_key: { group: 'system', key: 'deployment_mode_enabled' },
      },
    }),
  ]);

  const getSessionValue = (key: string, fallback: string) =>
    sessionSettings.find((s) => s.key === key)?.value ?? fallback;

  const sessionTimeoutMinutes = parseInt(
    getSessionValue('timeoutMinutes', '120'),
    10,
  );
  const idleCheckIntervalSeconds = parseInt(
    getSessionValue('idleCheckIntervalSeconds', '60'),
    10,
  );
  const warningBeforeMinutes = parseInt(
    getSessionValue('warningBeforeMinutes', '5'),
    10,
  );

  const deploymentModeEnabled = deploymentModeSetting?.value === 'true';

  return data(
    {
      user,
      systemName,
      isImpersonating: !!impersonatedUserId,
      originalUserId,
      sessionTimeoutMinutes,
      idleCheckIntervalSeconds,
      warningBeforeMinutes,
      notifications,
      unreadNotificationCount,
      deploymentModeEnabled,
      requestInfo: {
        hints: getHints(request),
        origin: getDomainUrl(request),
        path: new URL(request.url).pathname,
        userPrefs: {
          theme: getTheme(request),
        },
      },
      ENV: getEnv(),
      toast,
      honeyProps,
    },
    {
      headers: combineHeaders(
        { 'Server-Timing': timings.toString() },
        toastHeaders,
      ),
    },
  );
}

export const headers: Route.HeadersFunction = pipeHeaders;

function Document({
  children,
  nonce,
  theme = 'light',
  env = {},
}: {
  children: React.ReactNode;
  nonce: string;
  theme?: Theme;
  env?: Record<string, string | undefined>;
}) {
  const allowIndexing = ENV.ALLOW_INDEXING !== 'false';
  return (
    <html lang="en" className={`${theme} h-full overflow-x-hidden`}>
      <head>
        <ClientHintCheck nonce={nonce} />
        <Meta />
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width,initial-scale=1" />
        {allowIndexing ? null : (
          <meta name="robots" content="noindex, nofollow" />
        )}
        <Links />
      </head>
      <body className="bg-background text-foreground">
        {children}
        <script
          nonce={nonce}
          // suppressHydrationWarning is required for CSP nonces
          // Nonces must be unique per request for security, so they will always
          // differ between server render and client hydration. This is expected
          // behavior and doesn't affect functionality.
          suppressHydrationWarning
          dangerouslySetInnerHTML={{
            __html: `window.ENV = ${JSON.stringify(env)}`,
          }}
        />
        <ScrollRestoration nonce={nonce} />
        <Scripts nonce={nonce} />
        <TailwindIndicator />

        <div
          className="hidden"
          dangerouslySetInnerHTML={{
            __html: `
  ___  ____  ____  _  _      _____  ___  _     ___  _  _  ____  ___  ____ 
 | _ ||  _ ||  _ || || |    |_   _|| _ || |   | _ || \\\/ ||  _ || _ || ___|
 | _ ||  __/| _ < | || |__    | |  | _ || |__ | _ || || ||  __/| _ ||___ |
 |_|_||_|   |_|\\_\\|_||____|   |_|  |_|_||____||_|_||_||_||_|   |_|_||____|

   _   ___  ___ ___ _      _____  _    _      _   __  __ ___  _   ___ 
  \/_\\ | _ \\| _ \\_ _| |    |_   _|/_\\  | |    /_\\ |  \\\/  | _ \\\/_\\ \/ __|
 \/ _ \\|  _\/|   \/| || |__    | | / _ \\ | |__ / _ \\| |\\\/| |  _\/ _ \\\\__ \\
\/_\/ \\_\\_|  |_|_\\___|____|   |_|/_/ \\_\\|____/_/ \\_\\_|  |_|_|\/_\/ \\_\\___/
`,
          }}
        />
      </body>
    </html>
  );
}

export function Layout({ children }: { children: React.ReactNode }) {
  // if there was an error running the loader, data could be missing
  const data = useLoaderData<typeof loader | null>();
  const nonce = useNonce();
  // Use actual theme from loader, fallback to system preference from hints
  const theme =
    data?.requestInfo.userPrefs.theme ?? data?.requestInfo.hints.theme;
  return (
    <Document nonce={nonce} theme={theme} env={data?.ENV}>
      {children}
    </Document>
  );
}

function App() {
  const data = useLoaderData<typeof loader>();
  const {
    notification,
    hideNotification,
    showError,
    showSuccess,
    showWarning,
    showInfo,
  } = useAnimatedNotification();

  // Simple idle timeout - checks based on admin settings
  const idleTimeout = useSimpleIdleTimeout(
    data.user,
    data.idleCheckIntervalSeconds,
  );

  // Handle toast from loader data
  React.useEffect(() => {
    if (data.toast) {
      if (data.toast.type === 'success') {
        showSuccess(data.toast.title || 'Success', data.toast.description);
      } else if (data.toast.type === 'error') {
        showError(data.toast.title || 'Error', data.toast.description);
      } else {
        showSuccess(data.toast.title || 'Notification', data.toast.description);
      }
    }
  }, [data.toast, showSuccess, showError]);

  const handleLogoutNow = () => {
    // For idle timeout, redirect to login with session expired message and preserve URL
    const currentPath = window.location.pathname + window.location.search;
    const redirectUrl =
      currentPath !== '/'
        ? `/login?redirectTo=${encodeURIComponent(currentPath)}&message=session-expired`
        : '/login?message=session-expired';
    window.location.href = redirectUrl;
  };

  // Check if current route should show deployment overlay
  const currentPath = data.requestInfo.path;
  const isExcludedRoute =
    currentPath.startsWith('/admin') ||
    currentPath === '/ops/settings/global-preferences';
  const shouldShowDeploymentOverlay =
    data.deploymentModeEnabled && !isExcludedRoute;

  // Use actual theme from loader, fallback to system preference from hints
  const theme =
    data.requestInfo.userPrefs.theme ?? data.requestInfo.hints.theme;
  useToast(data.toast);

  return (
    <div className={theme === 'dark' ? 'dark' : ''}>
      <OpenImgContextProvider
        optimizerEndpoint="/resources/images"
        getSrc={getImgSrc}
      >
        <NotifyProvider
          showSuccess={showSuccess}
          showError={showError}
          showInfo={showInfo}
          showWarning={showWarning}
        >
          {data.isImpersonating && (
            <div className="fixed top-12 left-0 right-0 z-9999 bg-yellow-500 text-black px-4 py-2 text-center font-semibold flex items-center justify-center gap-4">
              <span>⚠️ You are currently viewing as {data.user?.nickname}</span>
              <Link
                to="/api/exit-impersonation"
                className="bg-black text-yellow-500 px-3 py-1 rounded text-sm font-bold hover:bg-gray-800 no-underline"
              >
                Exit Impersonation
              </Link>
            </div>
          )}
          <Outlet />
          {/* Idle timeout warning modal */}
          <IdleWarningModal
            isOpen={idleTimeout.showWarning}
            minutesLeft={idleTimeout.minutesLeft}
            secondsLeft={idleTimeout.secondsLeft}
            onExtendSession={idleTimeout.extendSession}
            onLogoutNow={handleLogoutNow}
          />

          <AnimatedNotification
            show={notification.show}
            kind={notification.kind}
            title={notification.title}
            messages={notification.messages}
            onClose={hideNotification}
            autoHideDelay={notification.config?.autoHideDelay}
            position={notification.config?.position}
            width={notification.config?.width}
            duration={notification.config?.duration}
            offsetFromEdge={notification.config?.offsetFromEdge}
          />
          <GlobalProgress />

          {/* Deployment Mode Overlay - Shows on all routes except admin and settings */}
          <DeploymentOverlay isEnabled={shouldShowDeploymentOverlay} />
        </NotifyProvider>
      </OpenImgContextProvider>
    </div>
  );
}

function AppWithProviders() {
  const data = useLoaderData<typeof loader>();
  return (
    <HoneypotProvider {...data.honeyProps}>
      <App />
    </HoneypotProvider>
  );
}

export default AppWithProviders;

// Custom error boundary with 404, 403, and 500 handlers
/** Only auto-reload once per window, so a persistent error can't loop. */
const STALE_BUILD_RELOAD_KEY = 'stale-build-reload-at';
const STALE_BUILD_RELOAD_COOLDOWN_MS = 30_000;

function hasRecentlyAutoReloaded(): boolean {
  try {
    const last = Number(sessionStorage.getItem(STALE_BUILD_RELOAD_KEY) ?? 0);
    return Date.now() - last < STALE_BUILD_RELOAD_COOLDOWN_MS;
  } catch {
    // sessionStorage can throw in private/blocked contexts — assume we have,
    // so we show the error rather than risk a reload loop.
    return true;
  }
}

export function ErrorBoundary() {
  const error = useRouteError();

  // A stale-build error is a client-side throw, never a server response, so
  // genuine 5xx responses are left alone.
  const shouldAutoReload =
    !isRouteErrorResponse(error) &&
    isStaleBuildOrNetworkError(error) &&
    typeof window !== 'undefined' &&
    !hasRecentlyAutoReloaded();

  // NOTE: this effect must stay above the 404/403 early returns below —
  // returning before a hook changes the hook count between renders and makes
  // React throw "rendered fewer hooks than expected".
  React.useEffect(() => {
    if (shouldAutoReload) {
      try {
        sessionStorage.setItem(STALE_BUILD_RELOAD_KEY, String(Date.now()));
      } catch {
        // Ignore: worst case we simply don't record the attempt.
      }
      window.location.reload();
      return;
    }

    // Only notify for actual server errors (500) and unexpected errors.
    // Stale-build/dropped-request errors are excluded: they are self-healing and
    // would otherwise drown real failures in noise. If one survives the reload
    // above (cooldown already used), it is persistent and does get reported.
    const shouldNotify =
      (isRouteErrorResponse(error) && error.status === 500) ||
      !isRouteErrorResponse(error);

    if (shouldNotify && typeof window !== 'undefined') {
      const errorMessage = isRouteErrorResponse(error)
        ? `${error.status} ${error.statusText}: ${error.data}`
        : error instanceof Error
          ? error.message
          : String(error);

      const errorStack = error instanceof Error ? error.stack : undefined;
      const url = window.location.href;

      // Create a unique key for this error (based on message + URL)
      const errorKey = `error-notified-${btoa(errorMessage + url).substring(0, 50)}`;

      // Check if we've already notified about this error in this session
      const alreadyNotified = sessionStorage.getItem(errorKey);

      if (!alreadyNotified) {
        // Mark as notified before sending to prevent race conditions
        sessionStorage.setItem(errorKey, Date.now().toString());

        // Send notification asynchronously (fire and forget)
        fetch('/api/notify-error', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            errorMessage,
            errorStack,
            url,
          }),
        }).catch((err) => {
          console.error('Failed to send error notification:', err);
          // Remove the flag if notification failed so it can retry
          sessionStorage.removeItem(errorKey);
        });
      }
    }
  }, [error, shouldAutoReload]);

  // Reloading to pick up the current build — show a calm message rather than
  // flashing a server-error screen for the moment before the reload lands.
  if (shouldAutoReload) {
    return (
      <div className="flex min-h-screen items-center justify-center p-6">
        <p className="text-sm text-muted-foreground">Updating to the latest version…</p>
      </div>
    );
  }

  // Handle 404 errors with custom NotFound component
  if (isRouteErrorResponse(error) && error.status === 404) {
    return <NotFound />;
  }

  // Handle 403 errors with custom Forbidden component
  if (isRouteErrorResponse(error) && error.status === 403) {
    return <Forbidden />;
  }

  // Handle 500 errors with custom ServerError component
  if (isRouteErrorResponse(error) && error.status === 500) {
    return <ServerError error={error} />;
  }

  // For all other errors (including non-Response errors), use custom ServerError component
  return <ServerError error={error} />;
}
